import React, { createContext, FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { GameEntity } from '@okletsplay/core';
import { TournamentEntity, MatchEntity } from 'app/interfaces/entity';
import { GameActivityType } from 'shared/enums';
import {
  useMatchPlatformFeePercentage,
  useTournamentPlatformFeePercentage,
} from 'shared/hooks/use-platform-fee-percentage';
import { AffiliateProgramConfig } from 'config/affiliate-program-config';
import { exchangeRatesSelector } from 'modules/payments/store/exchange-rates/selectors';
import { DEFAULT_GAME_ACTIVITY_PAYMENT_CONFIG } from 'modules/game-payments/constants';
import { GameActivityPaymentConfig } from 'modules/game-payments/interfaces';
import { buildGameActivityPaymentConfig } from 'modules/game-payments/utils';

type MatchActivity = Pick<MatchEntity, 'currencyType'> & { game?: Pick<GameEntity, 'id'> };
type TournamentActivity = Pick<TournamentEntity, 'currencyType' | 'isSponsored'> & {
  game?: Pick<GameEntity, 'id'>;
};

type ContextType = {
  paymentConfig: GameActivityPaymentConfig;
  platformFeePercentage: number;
  isRewardPaymentsEnabled: boolean;
};

export const GameActivityPaymentContext = createContext<ContextType>({
  paymentConfig: DEFAULT_GAME_ACTIVITY_PAYMENT_CONFIG,
  platformFeePercentage: 0,
  isRewardPaymentsEnabled: false,
});

interface ProviderProps {
  gameActivity?: MatchActivity | TournamentActivity;
  activityType: GameActivityType;
}

export const GameActivityPaymentProvider: FC<ProviderProps> = ({
  gameActivity,
  activityType,
  children,
}) => {
  const exchangeRates = useSelector(exchangeRatesSelector.all);

  const paymentConfig = useMemo(
    () =>
      buildGameActivityPaymentConfig({
        gameActivity,
        exchangeRates,
      }),
    [gameActivity, exchangeRates]
  );

  const { platformFeePercentage: matchPlatformFeePercentage } = useMatchPlatformFeePercentage();
  const { platformFeePercentage: tournamentPlatformFeePercentage } =
    useTournamentPlatformFeePercentage(gameActivity?.game?.id);

  const platformFeePercentage = useMemo(() => {
    switch (activityType) {
      case GameActivityType.Match:
        return matchPlatformFeePercentage;
      case GameActivityType.Tournament:
        return tournamentPlatformFeePercentage;
    }
  }, [activityType, matchPlatformFeePercentage, tournamentPlatformFeePercentage]);

  const isRewardPaymentsEnabled = useMemo(() => {
    switch (activityType) {
      case GameActivityType.Match:
        return false;
      case GameActivityType.Tournament:
        return (
          AffiliateProgramConfig.isJoinTournamentEnabled() &&
          Boolean((gameActivity as TournamentActivity)?.isSponsored)
        );
      default:
        return false;
    }
  }, [activityType, gameActivity]);

  return (
    <GameActivityPaymentContext.Provider
      value={{
        platformFeePercentage,
        isRewardPaymentsEnabled,
        paymentConfig,
      }}
    >
      {children}
    </GameActivityPaymentContext.Provider>
  );
};
